import React, { ChangeEvent, useCallback, useState } from 'react';
import { Button, Checkbox, notification } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shared/components/breadcrumbs/Breadcrumbs';
import { TDivisionResponse, TOrganizationResponse } from 'app/services/edpApiV1/moderator';
import moment from 'moment';
import { SERVER_DATE_MASK } from 'shared/constants';
import { IPollCreateRequest, TQuestionInPoll } from 'shared/types/poll.types';
import { PollAnswerType, PollTabs } from 'shared/enums/poll';
import { useCreatePollMutation } from 'app/services/edpApiV1/poll';
import { uiModel } from 'entities/ui';
import { checkDate } from 'shared/helpers/date';
import { returnErrorOrMessage } from 'shared/helpers/errors';
import ModalCancelSubmit from '../../../shared/components/modalCancelSubmit/ModalCancelSubmit';
import RequredField from '../../../shared/components/title/Title';
import QuestionDrawer from '../questionDrawer/QuestionDrawer';
import style from './index.module.scss';
import PickDivisions from './pickDivisions/PickDivisions';
import PollDates from './pollDates/PollDates';
import QuestionsSection from './questionsSection/QuestionsSection';

const breadCrumbs = [
  { name: 'Опросы', link: '/polls' },
  { name: 'Добавить опрос', link: `/polls/create` },
];

const CreatePoll = () => {
  const [openCancelModal, setOpenCanelModal] = useState<boolean>(false);
  const me = useAppSelector(state => state.entities.viewer.me);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>('');
  const [auto, setAuto] = useState<boolean>(false);
  const [pollQuestions, setPollQuestions] = useState<TQuestionInPoll[]>([]);

  const [text, setText] = useState<string>('');
  const [divisions, setDivisions] = useState<TDivisionResponse[]>([]);
  const [organizations, setOrganizations] = useState<TOrganizationResponse[]>([]);
  const [startTime, setStartTime] = useState<string | string[] | undefined>();
  const [endTime, setEndTime] = useState<string | string[] | undefined>();
  const [startDate, setStartDate] = useState<string | string[] | undefined>();
  const [endDate, setEndDate] = useState<string | string[] | undefined>();
  const [createPollQuery, { isLoading: isCreatePollLoading }] = useCreatePollMutation();
  const [isDrawerCreateOpen, setIsDrawerCreateOpen] = useState<boolean>(false);

  const onTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTitle(event.target.value);
    },
    [dispatch],
  );

  const onTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setText(event.target.value);
    },
    [dispatch],
  );

  const validatePoll = (): boolean => {
    let isDateCorrect;

    if (auto) {
      isDateCorrect = !!(startDate && startTime && endDate && endTime);
    } else {
      isDateCorrect = true;
    }
    return !!title && !!text && isDateCorrect && !!pollQuestions.length;
  };

  const onSubmitCancelModal = () => {
    setOpenCanelModal(false);
    navigate('/polls');
  };

  const onCancelCancelModal = () => {
    setOpenCanelModal(false);
  };

  const cancelModalOpen = () => {
    setOpenCanelModal(true);
  };

  const autoPollToggled = () => {
    setAuto(prev => !prev);
  };

  const preparePollToSave = () => {
    const preparedPollQuestions = pollQuestions.map(elem => {
      const { answerType, answers, isYourAnswer, title } = elem;
      const preparedAnswers = answers.map(item => item.value);
      return {
        answerType,
        answers: preparedAnswers,
        isYourAnswer,
        title,
      };
    });
    const poll: IPollCreateRequest = {
      created: moment().format(SERVER_DATE_MASK),
      title,
      content: text,
      startDate: checkDate(startDate as string, startTime as string, auto),
      stopDate: checkDate(endDate as string, endTime as string, auto),
      organizationIds: organizations.map(elem => elem.id),
      divisionIds: divisions.map(elem => elem.id),
      questions: preparedPollQuestions,
    };
    return poll;
  };

  const createPoll = async () => {
    await createPollQuery(preparePollToSave())
      .then((data: any) => {
        if (Object.keys(data).includes('error')) {
          notification.error({
            message: returnErrorOrMessage(data, 'Не удалось создать опрос'),
            placement: 'topRight',
          });
        } else {
          notification.success({
            message: 'Опрос создан успешно',
            placement: 'topRight',
          });
          dispatch(uiModel.Tabs.actions.setActive(PollTabs.UNPUBLICATED));
          navigate('/polls');
        }
      })
      .catch((e: any) => {
        notification.error({
          message: returnErrorOrMessage(e, 'Не удалось создать опрос'),
          placement: 'topRight',
        });
      });
  };

  const changeDivisions = (divisionsToChange: TDivisionResponse[]) => {
    setDivisions(divisionsToChange);
  };

  const changeOrganizations = (organizationsToChange: TOrganizationResponse[]) => {
    setOrganizations(organizationsToChange);
  };

  const onDrawerClose = () => {
    setIsDrawerCreateOpen(false);
  };

  const onCreate = (data: TQuestionInPoll) => {
    setPollQuestions([...pollQuestions, data]);
  };

  const openDrawer = () => {
    setIsDrawerCreateOpen(true);
  };

  const onDelete = (id: number) => {
    const filteredQuestions = pollQuestions.filter(elem => elem.id !== id);
    setPollQuestions(filteredQuestions);
  };

  const onUpdate = (data: TQuestionInPoll) => {
    const updatedPollQuestions = pollQuestions.map(elem => {
      if (elem.id === data.id) {
        return data;
      }
      return elem;
    });
    setPollQuestions(updatedPollQuestions);
  };

  return (
    <div className={style.wrap}>
      <div className={style['top-block']}>
        <h3>
          <Breadcrumbs breadCrumbs={breadCrumbs} />
        </h3>
        <Button onClick={cancelModalOpen}>Отмена</Button>
        <ModalCancelSubmit open={openCancelModal} onQuit={onSubmitCancelModal} onStay={onCancelCancelModal} />
      </div>
      <RequredField onTitleChange={onTitleChange} />
      <RequredField onTitleChange={onTextChange} label="Текст опроса" isTextarea placeholder="Введите текст опроса" />
      <QuestionsSection onOpen={openDrawer} questions={pollQuestions} onDelete={onDelete} onUpdate={onUpdate} />
      <QuestionDrawer isOpen={isDrawerCreateOpen} onClose={onDrawerClose} onCreate={onCreate} />
      <PickDivisions
        selectedDivisions={divisions}
        addDivisions={changeDivisions}
        addOrganizations={changeOrganizations}
        selectedOrganizations={organizations}
      />
      <div className={style.date}>
        <div className={style.deferred}>
          <Checkbox onChange={autoPollToggled}>Начало/окончание опроса по дате</Checkbox>
        </div>
        {auto ? (
          <PollDates
            onEndDateChange={setEndDate}
            onEndTimeChange={setEndTime}
            onStartDateChange={setStartDate}
            onStartTimeChange={setStartTime}
            startDate={startDate as string}
            endDate={endDate as string}
            startTime={startTime as string}
            endTime={endTime as string}
          />
        ) : null}
      </div>
      <div className={style['create-btn']}>
        <Button type="primary" onClick={createPoll} disabled={!validatePoll()} loading={isCreatePollLoading}>
          Создать
        </Button>
      </div>
    </div>
  );
};

export default CreatePoll;
